import { useIntl } from "react-intl";

export interface Formatters {
  /** Date and time in the user's display time zone (the IntlProvider's `timeZone`). */
  readonly dateTime: (iso: string) => string;
  readonly date: (iso: string) => string;
  /** Short table form `1 Oct 14:29` (design D1), in the user's display time zone. */
  readonly shortDateTime: (iso: string) => string;
  readonly number: (value: number) => string;
  /** `0.256` becomes `25.6%`. */
  readonly percent: (fraction: number) => string;
}

/**
 * Short date and time of a table cell: day, abbreviated month and 24-hour time (`1 Oct 14:29`,
 * `1 out 14:29`), day first in both locales as in the design, in the given IANA time zone. The
 * abbreviation comes from `Intl` per locale, without its trailing period. An invalid instant
 * gives `—`.
 */
export function formatShortDateTime(
  value: string | Date,
  locale: string,
  timeZone?: string,
): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "\u2014";
  const parts = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    ...(timeZone === undefined ? {} : { timeZone }),
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  const month = part("month").replace(/\.$/, "");
  return `${part("day")} ${month} ${part("hour")}:${part("minute")}`;
}

/** Locale-aware formatting of the UTC values the API returns (design D16). */
export function useFormatters(): Formatters {
  const intl = useIntl();
  return {
    dateTime: (iso) => intl.formatDate(iso, { dateStyle: "medium", timeStyle: "medium" }),
    date: (iso) => intl.formatDate(iso, { dateStyle: "medium" }),
    shortDateTime: (iso) => formatShortDateTime(iso, intl.locale, intl.timeZone),
    number: (value) => intl.formatNumber(value),
    percent: (fraction) =>
      intl.formatNumber(fraction, { style: "percent", maximumFractionDigits: 1 }),
  };
}
